import { toast } from '@hivebrite/design-system/Toast';

import type { Post } from 'hb-builder/components/UIComponents/page/dynamic/Slider/SliderLayouts/PostsWrapper/types';
import { POSTS_QUERY_KEY } from 'hb-react/backoffice/constants/builder/builderApiKeys';
import { POSTS_ROUTES } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicSearch/routes';
import type { FetchDynamicSearchByTypeArgs } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicSearch/types';
import { getApiDynamicComponentBaseEndpoint } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicSearch/utils/getApiDynamicComponentBaseEndpoint';
import useQueryOnce from 'hb-react/shared/hooks/useQueryOnce';
import { axiosInstance } from 'hb-react/shared/utils/axios';
import { APP_TYPE } from 'hb-react/types/appType';

const getPostsUrl = ({
  app,
  networkId,
  shouldUseNewEndpoints,
  topicId,
}: {
  app: APP_TYPE;
  networkId: number;
  shouldUseNewEndpoints?: boolean;
  topicId?: number | null;
}) => {
  if (app === APP_TYPE.BACKOFFICE) {
    return Routes.fetch_posts_backoffice_network_dynamic_components_search_index_path(
      networkId,
    );
  }

  // Only available endpoint available for the public office
  // Currently, in the dynamic component, the posts are not called on the PO
  if (app === APP_TYPE.PUBLIC_OFFICE) {
    return Routes.fetch_posts_dynamic_components_search_index_path();
  }

  return shouldUseNewEndpoints
    ? getApiDynamicComponentBaseEndpoint({
        app,
        topicId,
        routes: POSTS_ROUTES,
      })
    : Routes.fetch_posts_dynamic_components_search_index_path();
};

export const useSearchPosts = ({
  app,
  enabled,
  flags,
  ids,
  networkId,
  params,
  type,
}: FetchDynamicSearchByTypeArgs) => {
  const postsURL = getPostsUrl({
    app,
    networkId,
    shouldUseNewEndpoints: flags?.flagUjbDynamicComponentsPerf,
    topicId: ids.topic_id,
  });

  const queryFn = async (): Promise<Post[]> => {
    try {
      const { data } = await axiosInstance.get(postsURL, {
        params: {
          ...params,
          published: true,
          ...ids,
        },
      });

      return data?.posts;
    } catch (error) {
      toast.error(__('Sorry, an error occurred'));
      throw error;
    }
  };

  return useQueryOnce({
    queryKey: [POSTS_QUERY_KEY, app, params, ids.topic_id, networkId],
    queryFn,
    enabled: enabled && type === 'posts' && flags?.flagsLoaded,
  });
};
